import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Save, ArrowLeft, Trash2, Plus, FileDown, FileText } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const CITTA_118 = [
  'Messina', 'Palermo', 'Catania', 'Siracusa', 'Ragusa', 'Trapani', 'Agrigento', 'Caltanissetta', 'Enna',
  'Roma', 'Milano', 'Napoli', 'Torino', 'Bologna', 'Firenze', 'Bari', 'Genova', 'Verona', 'Venezia',
];

const CARATTERISTICHE = [
  "Al coperto", "All'aperto", "Localizzato e ben definito", "Esteso > 1 campo di calcio",
  "Non delimitato da recinzioni", "Delimitato da recinzioni", "Presenza di scale in entrata/uscita",
  "Recinzioni temporanee", "Ponteggio temporaneo / palco / coperture",
];
const LOGISTICA = ["Servizi igienici disponibili", "Disponibilità d'acqua", "Punto di ristoro"];
const ALTRE_VARIABILI = [
  "Prevista vendita/consumo di alcool", "Possibile consumo di droghe",
  "Presenza di categorie deboli (bambini, anziani, disabili)", "Evento ampiamente pubblicizzato dai media",
  "Presenza di figure politiche-religiose", "Possibili difficoltà nella viabilità",
  "Presenza di tensioni socio-politiche",
];

const emptyMezzo = { associazione: '', tipologia: 'MSB', targa: '' };
const emptyMedico = { cognome: '', nome: '', qualifica: 'Medico', matricola: '' };
const emptySocc = { cognome: '', nome: '', attestato: '' };

const defaultPiano = {
  destinatario_citta: 'Messina',
  dichiarante_nome: '', dichiarante_cognome: '', dichiarante_nato_a: '', dichiarante_nato_il: '',
  dichiarante_codice_fiscale: '', dichiarante_telefono: '', dichiarante_email: '',
  residenza_via: '', residenza_cap: '', residenza_citta: '', residenza_provincia: '',
  qualifica: 'legale rappresentante', qualifica_altro: '',
  associazione_denominazione: '', associazione_codice_fiscale: '', associazione_piva: '',
  associazione_sede_via: '', associazione_sede_cap: '', associazione_sede_citta: '', associazione_sede_provincia: '',
  evento_titolo: '', evento_data: '', evento_data_fine: '', evento_orario: '',
  evento_descrizione: '', evento_luogo_indirizzo: '', evento_luogo_citta: '',
  periodicita: 'occasionale', durata: 'meno_12h', luogo_tipo: 'citta',
  densita_partecipanti: 'media', eta_prevalente: '25_65',
  condizione_partecipanti: 'rilassato', posizione_partecipanti: 'in_piedi',
  caratteristiche_luogo: [], logistica_area: [], altre_variabili: [],
  maurer: {
    capienza_max: 0, visitatori_previsti: 0, tipo_manifestazione: 'intrattenimento',
    presenza_personalita: 0, problemi_ordine_pubblico: false,
  },
  mezzi: [], medici: [], soccorritori: [],
  cert_mezzi_anni_7: false, cert_mezzi_km_250k: false, cert_autorizzazione_asp: false,
  cert_personale_idoneita: false, cert_personale_da_1961: false,
  stato: 'bozza', note: '',
};

export default function PianoSanitarioDetailPage() {
  const { piano_id } = useParams();
  const nav = useNavigate();
  const isNew = piano_id === 'nuovo';
  const [data, setData] = useState(defaultPiano);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [rischio, setRischio] = useState(null);

  useEffect(() => {
    if (!isNew) {
      api.get(`/sicurezza/piani-sanitari/${piano_id}`)
        .then(r => { setData({ ...defaultPiano, ...r.data }); setRischio(r.data.rischio); })
        .catch(e => toast.error('Impossibile caricare: ' + (e?.response?.data?.detail || e.message)))
        .finally(() => setLoading(false));
    }
  }, [piano_id, isNew]);

  // Calcolo rischio real-time
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data: r } = await api.post('/sicurezza/piani-sanitari/calcola-rischio', data.maurer);
        setRischio(r);
      } catch (e) { /* silent */ }
    }, 400);
    return () => clearTimeout(t);
  }, [data.maurer]);

  const upd = (field, val) => setData(d => ({ ...d, [field]: val }));
  const updMaurer = (field, val) => setData(d => ({ ...d, maurer: { ...d.maurer, [field]: val } }));
  const toggleMulti = (field, item) => setData(d => ({
    ...d, [field]: d[field].includes(item) ? d[field].filter(x => x !== item) : [...d[field], item],
  }));

  const save = async (download) => {
    if (!data.evento_titolo) { toast.error('Titolo evento obbligatorio'); return; }
    if (!data.dichiarante_nome || !data.dichiarante_cognome) { toast.error('Nome+Cognome dichiarante obbligatori'); return; }
    setSaving(true);
    try {
      let result;
      if (isNew) {
        const { data: r } = await api.post('/sicurezza/piani-sanitari', data);
        result = r;
        toast.success('Piano creato');
        if (!download) nav(`/sicurezza/piani-sanitari/${r.id}`);
      } else {
        const { data: r } = await api.put(`/sicurezza/piani-sanitari/${piano_id}`, data);
        result = r;
        toast.success('Piano aggiornato');
      }
      setRischio(result.rischio);
      if (download) {
        await downloadFile(result.id, download);
      }
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const downloadFile = async (id, fmt) => {
    try {
      const resp = await api.get(`/sicurezza/piani-sanitari/${id}/${fmt}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const ext = fmt === 'pdf' ? 'pdf' : 'docx';
      a.download = `PianoSanitario_${data.evento_titolo || 'piano'}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} scaricato`);
    } catch (e) {
      toast.error(`Errore ${fmt.toUpperCase()}: ` + (e?.response?.data?.detail || e.message));
    }
  };

  const classeColor = useMemo(() => {
    const c = rischio?.classe;
    if (c === 'A') return 'bg-green-100 text-green-900 border-green-400';
    if (c === 'B') return 'bg-amber-100 text-amber-900 border-amber-400';
    if (c === 'C') return 'bg-red-100 text-red-900 border-red-400';
    return 'bg-slate-100 text-slate-700 border-slate-300';
  }, [rischio]);

  if (loading) return <div className="p-8 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;

  return (
    <div className="space-y-4 p-4 max-w-6xl mx-auto pb-32">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => nav('/sicurezza/piani-sanitari')}><ArrowLeft className="w-4 h-4" /></Button>
          <h1 className="text-xl font-bold" data-testid="piano-detail-title">{isNew ? 'Nuovo Piano Sanitario' : `Piano: ${data.evento_titolo || '(senza titolo)'}`}</h1>
        </div>
      </div>

      {/* RISCHIO BANNER (sticky top) */}
      {rischio && (
        <div className={`sticky top-0 z-10 p-3 rounded-lg border-2 ${classeColor} flex items-center justify-between flex-wrap gap-2 shadow-sm`} data-testid="rischio-banner">
          <div>
            <div className="text-sm font-bold">Rischio calcolato (Algoritmo di Maurer): <span className="text-2xl ml-2">Classe {rischio.classe}</span> <span className="ml-1 text-base">({rischio.totale} punti)</span></div>
            <div className="text-xs mt-1">{rischio.classe_descrizione}</div>
          </div>
          <div className="text-xs font-mono">A={rischio.A} · B={rischio.B} · C={rischio.C} · D={rischio.D} · E={rischio.E}</div>
        </div>
      )}

      {/* SEZIONE 1: DESTINATARIO */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">1. Destinatario</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Label className="text-xs">Direttore C.O. 118 di *</Label>
          <Select value={data.destinatario_citta} onValueChange={v => upd('destinatario_citta', v)}>
            <SelectTrigger className="max-w-xs" data-testid="dest-city-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CITTA_118.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* SEZIONE 2: DICHIARANTE */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">2. Dati del Dichiarante</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div><Label className="text-xs">Nome *</Label><Input value={data.dichiarante_nome} onChange={e => upd('dichiarante_nome', e.target.value)} data-testid="dichiarante-nome" /></div>
          <div><Label className="text-xs">Cognome *</Label><Input value={data.dichiarante_cognome} onChange={e => upd('dichiarante_cognome', e.target.value)} data-testid="dichiarante-cognome" /></div>
          <div><Label className="text-xs">Codice Fiscale</Label><Input value={data.dichiarante_codice_fiscale} onChange={e => upd('dichiarante_codice_fiscale', e.target.value.toUpperCase())} className="font-mono" maxLength={16} /></div>
          <div><Label className="text-xs">Nato a</Label><Input value={data.dichiarante_nato_a} onChange={e => upd('dichiarante_nato_a', e.target.value)} /></div>
          <div><Label className="text-xs">Nato il (gg/mm/aaaa)</Label><Input value={data.dichiarante_nato_il} onChange={e => upd('dichiarante_nato_il', e.target.value)} placeholder="01/01/1980" /></div>
          <div><Label className="text-xs">Telefono</Label><Input value={data.dichiarante_telefono} onChange={e => upd('dichiarante_telefono', e.target.value)} /></div>
          <div><Label className="text-xs">Residenza — Via</Label><Input value={data.residenza_via} onChange={e => upd('residenza_via', e.target.value)} /></div>
          <div><Label className="text-xs">CAP</Label><Input value={data.residenza_cap} onChange={e => upd('residenza_cap', e.target.value)} maxLength={5} /></div>
          <div><Label className="text-xs">Città</Label><Input value={data.residenza_citta} onChange={e => upd('residenza_citta', e.target.value)} /></div>
          <div><Label className="text-xs">Provincia</Label><Input value={data.residenza_provincia} onChange={e => upd('residenza_provincia', e.target.value.toUpperCase())} maxLength={2} /></div>
          <div><Label className="text-xs">Qualifica</Label>
            <Select value={data.qualifica} onValueChange={v => upd('qualifica', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="legale rappresentante">Legale rappresentante</SelectItem>
                <SelectItem value="presidente">Presidente</SelectItem>
                <SelectItem value="responsabile">Responsabile</SelectItem>
                <SelectItem value="altro">Altro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {data.qualifica === 'altro' && <div><Label className="text-xs">Specifica</Label><Input value={data.qualifica_altro} onChange={e => upd('qualifica_altro', e.target.value)} /></div>}
        </CardContent>
      </Card>

      {/* SEZIONE 3: ASSOCIAZIONE */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">3. Organizzazione / Associazione</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="col-span-2 md:col-span-3"><Label className="text-xs">Denominazione *</Label><Input value={data.associazione_denominazione} onChange={e => upd('associazione_denominazione', e.target.value)} data-testid="assoc-denom" /></div>
          <div><Label className="text-xs">C.F.</Label><Input value={data.associazione_codice_fiscale} onChange={e => upd('associazione_codice_fiscale', e.target.value.toUpperCase())} className="font-mono" /></div>
          <div><Label className="text-xs">P. IVA</Label><Input value={data.associazione_piva} onChange={e => upd('associazione_piva', e.target.value)} className="font-mono" /></div>
          <div></div>
          <div className="col-span-2"><Label className="text-xs">Sede legale — Via</Label><Input value={data.associazione_sede_via} onChange={e => upd('associazione_sede_via', e.target.value)} /></div>
          <div><Label className="text-xs">CAP</Label><Input value={data.associazione_sede_cap} onChange={e => upd('associazione_sede_cap', e.target.value)} maxLength={5} /></div>
          <div><Label className="text-xs">Città</Label><Input value={data.associazione_sede_citta} onChange={e => upd('associazione_sede_citta', e.target.value)} /></div>
          <div><Label className="text-xs">Provincia</Label><Input value={data.associazione_sede_provincia} onChange={e => upd('associazione_sede_provincia', e.target.value.toUpperCase())} maxLength={2} /></div>
        </CardContent>
      </Card>

      {/* SEZIONE 4: EVENTO */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">4. Dati Evento</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="col-span-2 md:col-span-3"><Label className="text-xs">Titolo evento *</Label><Input value={data.evento_titolo} onChange={e => upd('evento_titolo', e.target.value)} data-testid="evento-titolo" /></div>
          <div><Label className="text-xs">Data inizio (gg/mm/aaaa)</Label><Input value={data.evento_data} onChange={e => upd('evento_data', e.target.value)} placeholder="15/06/2026" data-testid="evento-data" /></div>
          <div><Label className="text-xs">Data fine (se pluri-giornata)</Label><Input value={data.evento_data_fine} onChange={e => upd('evento_data_fine', e.target.value)} placeholder="(opzionale)" /></div>
          <div><Label className="text-xs">Orario</Label><Input value={data.evento_orario} onChange={e => upd('evento_orario', e.target.value)} placeholder="es. 18:00 - 24:00" /></div>
          <div className="col-span-2"><Label className="text-xs">Luogo (indirizzo)</Label><Input value={data.evento_luogo_indirizzo} onChange={e => upd('evento_luogo_indirizzo', e.target.value)} /></div>
          <div><Label className="text-xs">Città</Label><Input value={data.evento_luogo_citta} onChange={e => upd('evento_luogo_citta', e.target.value)} /></div>
          <div className="col-span-2 md:col-span-3"><Label className="text-xs">Descrizione</Label><Textarea value={data.evento_descrizione} onChange={e => upd('evento_descrizione', e.target.value)} rows={2} /></div>
        </CardContent>
      </Card>

      {/* SEZIONE 5: ALGORITMO MAURER */}
      <Card className="border-blue-300 bg-blue-50/30">
        <CardHeader className="pb-2"><CardTitle className="text-sm">5. Algoritmo di Maurer — Calcolo Rischio (in tempo reale)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><Label className="text-xs">A — Capienza max luogo</Label><Input type="number" value={data.maurer.capienza_max} onChange={e => updMaurer('capienza_max', parseInt(e.target.value) || 0)} data-testid="maurer-capienza" /></div>
            <div><Label className="text-xs">B — Visitatori previsti</Label><Input type="number" value={data.maurer.visitatori_previsti} onChange={e => updMaurer('visitatori_previsti', parseInt(e.target.value) || 0)} data-testid="maurer-visitatori" /></div>
            <div><Label className="text-xs">C — Tipo manifestazione</Label>
              <Select value={data.maurer.tipo_manifestazione} onValueChange={v => updMaurer('tipo_manifestazione', v)}>
                <SelectTrigger data-testid="maurer-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="religioso">Religioso (0.9)</SelectItem>
                  <SelectItem value="sportivo">Sportivo (1.0)</SelectItem>
                  <SelectItem value="intrattenimento">Intrattenimento (1.0)</SelectItem>
                  <SelectItem value="politico_sociale">Politico/sociale (1.2)</SelectItem>
                  <SelectItem value="concerto_pop_rock">Concerto pop/rock (1.5)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">D — Numero personalità (÷5)</Label><Input type="number" value={data.maurer.presenza_personalita} onChange={e => updMaurer('presenza_personalita', parseInt(e.target.value) || 0)} /></div>
            <div className="flex items-end gap-2 pb-1">
              <input type="checkbox" id="op" checked={data.maurer.problemi_ordine_pubblico} onChange={e => updMaurer('problemi_ordine_pubblico', e.target.checked)} className="w-4 h-4" />
              <Label htmlFor="op" className="text-xs cursor-pointer">E — Problemi ordine pubblico (+10)</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SEZIONE 6: VARIABILI */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">6. Variabili di Valutazione</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">Periodicità</Label>
            <Select value={data.periodicita} onValueChange={v => upd('periodicita', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="annuale">Annuale</SelectItem>
              <SelectItem value="mensile">Mensile</SelectItem>
              <SelectItem value="giornaliera">Giornaliera</SelectItem>
              <SelectItem value="occasionale">Occasionale</SelectItem>
            </SelectContent></Select>
          </div>
          <div><Label className="text-xs">Durata</Label>
            <Select value={data.durata} onValueChange={v => upd('durata', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="meno_12h">&lt; 12 ore</SelectItem>
              <SelectItem value="12h_3gg">12h - 3 giorni</SelectItem>
              <SelectItem value="oltre_3gg">&gt; 3 giorni</SelectItem>
            </SelectContent></Select>
          </div>
          <div><Label className="text-xs">Luogo</Label>
            <Select value={data.luogo_tipo} onValueChange={v => upd('luogo_tipo', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="citta">Città</SelectItem>
              <SelectItem value="periferia">Periferia</SelectItem>
              <SelectItem value="acquatico">Acquatico</SelectItem>
              <SelectItem value="altro">Altro</SelectItem>
            </SelectContent></Select>
          </div>
          <div><Label className="text-xs">Densità (persone/m²)</Label>
            <Select value={data.densita_partecipanti} onValueChange={v => upd('densita_partecipanti', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="bassa">Bassa (1-2)</SelectItem>
              <SelectItem value="media">Media (3-4)</SelectItem>
              <SelectItem value="alta">Alta (5-8)</SelectItem>
              <SelectItem value="estrema">Estrema (&gt;8)</SelectItem>
            </SelectContent></Select>
          </div>
          <div><Label className="text-xs">Età prevalente</Label>
            <Select value={data.eta_prevalente} onValueChange={v => upd('eta_prevalente', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="25_65">25-65</SelectItem>
              <SelectItem value="altre">&lt; 25 oppure &gt; 65</SelectItem>
            </SelectContent></Select>
          </div>
          <div><Label className="text-xs">Condizione partecipanti</Label>
            <Select value={data.condizione_partecipanti} onValueChange={v => upd('condizione_partecipanti', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="rilassato">Rilassato</SelectItem>
              <SelectItem value="eccitato">Eccitato</SelectItem>
              <SelectItem value="aggressivo">Aggressivo</SelectItem>
            </SelectContent></Select>
          </div>
          <div><Label className="text-xs">Posizione</Label>
            <Select value={data.posizione_partecipanti} onValueChange={v => upd('posizione_partecipanti', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
              <SelectItem value="seduti">Seduti</SelectItem>
              <SelectItem value="parte_seduti">In parte seduti</SelectItem>
              <SelectItem value="in_piedi">In piedi</SelectItem>
            </SelectContent></Select>
          </div>
        </CardContent>
      </Card>

      {/* CHECKBOX MULTI */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">7. Caratteristiche e logistica (selezione multipla)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="font-medium mb-2">Caratteristiche del luogo</p>
            {CARATTERISTICHE.map(c => (
              <label key={c} className="flex items-center gap-2 cursor-pointer text-xs py-0.5">
                <input type="checkbox" checked={data.caratteristiche_luogo.includes(c)} onChange={() => toggleMulti('caratteristiche_luogo', c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>
          <div>
            <p className="font-medium mb-2">Logistica area</p>
            {LOGISTICA.map(c => (
              <label key={c} className="flex items-center gap-2 cursor-pointer text-xs py-0.5">
                <input type="checkbox" checked={data.logistica_area.includes(c)} onChange={() => toggleMulti('logistica_area', c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>
          <div>
            <p className="font-medium mb-2">Altre variabili</p>
            {ALTRE_VARIABILI.map(c => (
              <label key={c} className="flex items-center gap-2 cursor-pointer text-xs py-0.5">
                <input type="checkbox" checked={data.altre_variabili.includes(c)} onChange={() => toggleMulti('altre_variabili', c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* TABELLE RISORSE */}
      {[
        { key: 'mezzi', title: 'Tabella Mezzi', empty: emptyMezzo, cols: [['associazione', 'Associazione'], ['tipologia', 'MSA/MSI/MSB'], ['targa', 'Targa']] },
        { key: 'medici', title: 'Tabella Medici / Infermieri', empty: emptyMedico, cols: [['cognome', 'Cognome'], ['nome', 'Nome'], ['qualifica', 'Qualifica'], ['matricola', 'Matricola']] },
        { key: 'soccorritori', title: 'Tabella Soccorritori', empty: emptySocc, cols: [['cognome', 'Cognome'], ['nome', 'Nome'], ['attestato', 'N° Attestato OTSEA/TSSA']] },
      ].map(t => (
        <Card key={t.key}>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">{t.title}</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setData(d => ({ ...d, [t.key]: [...d[t.key], { ...t.empty }] }))} data-testid={`add-${t.key}`}>
              <Plus className="w-3 h-3 mr-1" /> Aggiungi
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {data[t.key].length === 0 ? (
              <p className="text-xs text-slate-500 p-3 italic">Nessuna voce. Clicca "Aggiungi" per inserirne una.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>{t.cols.map(([_, label]) => <th key={label} className="p-2 text-xs text-left">{label}</th>)}<th className="p-2 w-10"></th></tr>
                </thead>
                <tbody>
                  {data[t.key].map((row, i) => (
                    <tr key={i} className="border-b">
                      {t.cols.map(([k]) => (
                        <td key={k} className="p-1"><Input value={row[k]} onChange={e => setData(d => ({ ...d, [t.key]: d[t.key].map((r, ix) => ix === i ? { ...r, [k]: e.target.value } : r) }))} className="h-8 text-sm" /></td>
                      ))}
                      <td className="p-1 text-right"><Button size="sm" variant="ghost" className="text-red-600" onClick={() => setData(d => ({ ...d, [t.key]: d[t.key].filter((_, ix) => ix !== i) }))}><Trash2 className="w-3 h-3" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ))}

      {/* AUTOCERTIFICAZIONE */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Autocertificazione associazione di volontariato</CardTitle></CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p className="font-medium mb-1">1. Mezzi di soccorso:</p>
          <label className="flex items-center gap-2 cursor-pointer text-xs"><input type="checkbox" checked={data.cert_mezzi_anni_7} onChange={e => upd('cert_mezzi_anni_7', e.target.checked)} /> Immatricolati per la prima volta da meno di 7 anni</label>
          <label className="flex items-center gap-2 cursor-pointer text-xs"><input type="checkbox" checked={data.cert_mezzi_km_250k} onChange={e => upd('cert_mezzi_km_250k', e.target.checked)} /> Con percorrenza inferiore a 250.000 km</label>
          <label className="flex items-center gap-2 cursor-pointer text-xs"><input type="checkbox" checked={data.cert_autorizzazione_asp} onChange={e => upd('cert_autorizzazione_asp', e.target.checked)} /> Autorizzazione sanitaria A.S.P. in corso di validità</label>
          <p className="font-medium mt-3 mb-1">2. Personale di volontariato munito di:</p>
          <label className="flex items-center gap-2 cursor-pointer text-xs"><input type="checkbox" checked={data.cert_personale_idoneita} onChange={e => upd('cert_personale_idoneita', e.target.checked)} /> Certificato di idoneità fisica rilasciato dal medico competente</label>
          <label className="flex items-center gap-2 cursor-pointer text-xs"><input type="checkbox" checked={data.cert_personale_da_1961} onChange={e => upd('cert_personale_da_1961', e.target.checked)} /> Certificazione ai sensi del D.A. n. 1961 del 29/10/2018</label>
        </CardContent>
      </Card>

      {/* AZIONI - sticky bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t shadow-lg p-3 flex items-center justify-end gap-2 z-20">
        <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Salva
        </Button>
        {!isNew && (
          <>
            <Button variant="outline" onClick={() => downloadFile(piano_id, 'docx')} data-testid="dl-docx-btn"><FileText className="w-4 h-4 mr-1" /> Scarica Word</Button>
            <Button onClick={() => downloadFile(piano_id, 'pdf')} className="bg-red-600 hover:bg-red-700" data-testid="dl-pdf-btn"><FileDown className="w-4 h-4 mr-1" /> Scarica PDF</Button>
          </>
        )}
        {isNew && (
          <Button onClick={() => save('docx')} disabled={saving} className="bg-blue-600"><FileText className="w-4 h-4 mr-1" /> Salva e Scarica Word</Button>
        )}
      </div>
    </div>
  );
}
